'use client';

import type { Currency } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useGetMatch, useGetPrize, useGetRank, useGetWinners } from '..';
import type { OddsEntry } from '../../types';

type UseMapPrizeTablesStaticPrizeProps = {
  data: OddsEntry[];
  currency: Currency;
  namespace: [lotteryNamespace: string, generalNamespace: string];
  hideWinners: boolean;
};

/**
 * Maps lottery odds data into a format suitable for displaying prize tables for lotteries, where prizes should resolve static keys.
 * Logic is built around the fact, that prize static key for specific rank is available in the translations. If not, value from API response is used.
 *
 * @param data - The array of odds entries for the lottery and any extra games.
 * @param currency - The currency to display prize amounts in.
 * @param namespace - Tuple containing the lottery and general translation namespaces.
 * @param hideWinners - (Optional) Use 'true' to hide winners.
 * @returns An array of objects, each representing a prize table for a lottery or extra game.
 */
function useMapPrizeTablesStaticPrize({ data, currency, namespace, hideWinners }: UseMapPrizeTablesStaticPrizeProps) {
  const [lottery, general] = namespace;
  const t = useTranslations(lottery);
  const getRank = useGetRank(lottery);
  const getMatch = useGetMatch(lottery);
  const getWinners = useGetWinners(general);
  const { default: getPrize } = useGetPrize(general);

  return (data ?? []).map(({ lotteryId, ranks }) => ({
    lotteryId,
    data: Object.entries(ranks ?? {}).map(([rank, conditions]) => {
      const hasPrizeStaticKey = t.has(`results_static_prize_${rank}`);
      return {
        rank: getRank(Number(rank)),
        match: getMatch(conditions[0]!.matches),
        ...(hideWinners ? {} : { winners: getWinners(conditions[0]?.winners) }),
        amount: hasPrizeStaticKey ? t(`results_static_prize_${rank}`) : getPrize(conditions[0]?.prize?.[currency]),
      };
    }),
  }));
}

export { useMapPrizeTablesStaticPrize };
